/**
 * NCR Edit platform options: the classification dropdown options, split out
 * of useNCREdit.
 */

import { useEffect, useState } from 'react';
import {
  qualityAPI,
  fetchAllPages,
  SourceOfRejection,
  CommonisedCorrectiveAction,
  FaultCodeGroup,
  FaultCodeCategory,
} from '../../lib/api';

/**
 * Loads the active classification options (sources of rejection, commonised
 * corrective actions, fault groups and categories) once on mount.
 *
 * @returns the four classification option lists
 */
export function useNCRPlatformOptions() {
  // Classification dropdown options
  const [sourcesOfRejection, setSourcesOfRejection] = useState<SourceOfRejection[]>([]);
  const [commonisedActions, setCommonisedActions] = useState<CommonisedCorrectiveAction[]>([]);
  const [faultGroups, setFaultGroups] = useState<FaultCodeGroup[]>([]);
  const [faultCategories, setFaultCategories] = useState<FaultCodeCategory[]>([]);

  // 24 Sep 2026: the NCR template switch is retired (one workflow for
  // everyone), so the page no longer reads platform settings for it.
  useEffect(() => {
    const fetchClassificationOptions = async () => {
      try {
        // Walk pages — the backend clamps page_size, so one request silently truncated.
        const [sors, fgs, fcs, ccas] = await Promise.all([
          fetchAllPages<SourceOfRejection>((page) => qualityAPI.getSourcesOfRejection({ is_active: true, page_size: 100, page })),
          fetchAllPages<FaultCodeGroup>((page) => qualityAPI.getFaultGroups({ is_active: true, page_size: 100, page })),
          fetchAllPages<FaultCodeCategory>((page) => qualityAPI.getFaultCategories({ is_active: true, page_size: 100, page })),
          fetchAllPages<CommonisedCorrectiveAction>((page) => qualityAPI.getCommonisedCorrectiveActions({ is_active: true, page_size: 100, page })),
        ]);
        setSourcesOfRejection(sors);
        setFaultGroups(fgs);
        setFaultCategories(fcs);
        setCommonisedActions(ccas);
      } catch {
        console.error('Error fetching classification options');
      }
    };

    fetchClassificationOptions();
  }, []);

  return { sourcesOfRejection, commonisedActions, faultGroups, faultCategories };
}
